export const transposeTableBlock = (tableBlock) => {
    try {
        const lines = tableBlock.trim().split(/\r?\n/);
        if (lines.length < 5) return tableBlock; // Too short to matter

        // Parse cells
        const matrix = lines.map(line => {
            const content = line.trim().replace(/^\|/, '').replace(/\|$/, '');
            return content.split('|').map(c => c.trim());
        });

        // Valid matrix check
        const validMatrix = matrix.filter(row => row.length > 0 && row.some(cell => cell !== ""));
        if (validMatrix.length === 0) return tableBlock;

        const colCount = validMatrix[0].length;

        // STRICT Condition: 2 columns
        if (colCount !== 2) return tableBlock;

        // Check data rows
        const dataRows = validMatrix.slice(2);
        if (!dataRows.every(row => row.length === 2)) return tableBlock;

        // Remove separator (index 1)
        const dataMatrix = validMatrix.filter((_, i) => i !== 1);

        // Transpose
        const newRow1 = dataMatrix.map(row => row[0]);
        const newRow2 = dataMatrix.map(row => row[1]);

        const header = "| " + newRow1.join(" | ") + " |";
        const separator = "| " + newRow1.map(() => "---").join(" | ") + " |";
        const values = "| " + newRow2.join(" | ") + " |";

        return `${header}\n${separator}\n${values}`;
    } catch (e) {
        return tableBlock;
    }
};

const fixInlineNumberedLists = (text) => {
    // Find all occurrences of "N. " where N is a number
    const pattern = /(\d+)\.\s+/g;
    const matches = [];
    let m;
    while ((m = pattern.exec(text)) !== null) {
        matches.push({ num: parseInt(m[1], 10), index: m.index });
    }

    if (matches.length < 3) return text;

    // Check if we have at least 3 consecutive numbers in sequence
    let consecutive = 1;
    for (let i = 1; i < matches.length; i++) {
        if (matches[i].num === matches[i - 1].num + 1) {
            consecutive++;
            if (consecutive >= 3) break;
        } else {
            consecutive = 1;
        }
    }

    if (consecutive < 3) return text;

    // Split before each numbered item, but avoid splitting inside table rows (|)
    let processed = text.replace(/([^|\n])\s+(?=\d+\.\s)/g, '$1\n');

    // If the first line doesn't start with a number but the second starts with "2. ", prepend "1. "
    const lines = processed.split('\n');
    if (lines.length >= 2 && !lines[0].trim().match(/^\d+\./) && lines[1].trim().match(/^2\.\s/)) {
        lines[0] = '1. ' + lines[0].trim();
        processed = lines.join('\n');
    }

    return processed;
};

const normalizeTableishLine = (line) => {
    if (typeof line !== 'string') return '';

    let normalized = line.replace(/^\s*[-*]\s+(?=\|)/, '').trim();

    if (normalized.startsWith('|') && !normalized.endsWith('|')) {
        normalized += ' |';
    }

    return normalized;
};

const parseTableCells = (row) =>
    normalizeTableishLine(row)
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map((cell) => cell.trim());

const isSeparatorRow = (line) => /^\|[\s\-:|]+\|$/.test(normalizeTableishLine(line));
const isSeparatorFragment = (line) => /^[-:|\s]+$/.test(String(line).trim()) && String(line).includes('-');
const isTableNoiseLine = (line) => /^[\s]*[-*•·▪◦]+[\s]*$/.test(String(line).trim());

const isTableishLine = (line) => /^\|.*\|$/.test(normalizeTableishLine(line));

const repairTableBlockByColumnCount = (blockLines) => {
    const normalizedLines = blockLines
        .map(normalizeTableishLine)
        .filter((line) => line.trim() !== '' && !isTableNoiseLine(line));

    if (normalizedLines.length < 2) return blockLines.join('\n');

    const headerIndex = normalizedLines.findIndex((line) => isTableishLine(line) && !isSeparatorRow(line));
    if (headerIndex === -1) return normalizedLines.join('\n');

    const headerCells = parseTableCells(normalizedLines[headerIndex]);
    const expectedColumns = headerCells.length;
    if (expectedColumns < 2) return normalizedLines.join('\n');

    const separatorIndex = normalizedLines.findIndex((line, idx) => idx > headerIndex && (isSeparatorRow(line) || isSeparatorFragment(line)));
    if (separatorIndex === -1) return normalizedLines.join('\n');

    const repaired = normalizedLines.slice(0, headerIndex + 1);
    repaired.push(`| ${new Array(expectedColumns).fill('---').join(' | ')} |`);
    let pendingCells = [];

    let dataStartIndex = separatorIndex + 1;
    while (dataStartIndex < normalizedLines.length && (isSeparatorRow(normalizedLines[dataStartIndex]) || isSeparatorFragment(normalizedLines[dataStartIndex]))) {
        dataStartIndex++;
    }

    const flushPending = () => {
        if (pendingCells.length === 0) return;

        const rowCells = pendingCells.slice(0, expectedColumns);
        while (rowCells.length < expectedColumns) {
            rowCells.push('-');
        }
        repaired.push(`| ${rowCells.join(' | ')} |`);
        pendingCells = pendingCells.slice(expectedColumns);
    };

    for (let i = dataStartIndex; i < normalizedLines.length; i++) {
        const line = normalizedLines[i];

        if (!isTableishLine(line)) {
            flushPending();
            repaired.push(line);
            continue;
        }

        const cells = parseTableCells(line);
        if (cells.length === expectedColumns) {
            if (pendingCells.length > 0) {
                flushPending();
            }
            repaired.push(`| ${cells.join(' | ')} |`);
            continue;
        }

        pendingCells.push(...cells);

        while (pendingCells.length >= expectedColumns) {
            flushPending();
        }
    }

    flushPending();

    return repaired.join('\n');
};

const repairBrokenTables = (text) => {
    const lines = text.split('\n');
    const repairedLines = [];
    let blockLines = [];

    const flushBlock = () => {
        if (blockLines.length === 0) return;
        repairedLines.push(repairTableBlockByColumnCount(blockLines));
        blockLines = [];
    };

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmed = line.trim();
        const tableish = isTableishLine(line) || /^\s*[-*]\s+\|/.test(line) || isSeparatorFragment(line);

        if (tableish) {
            blockLines.push(line);
            continue;
        }

        if (blockLines.length > 0 && (trimmed === '' || isTableNoiseLine(line))) {
            blockLines.push(line);
            continue;
        }

        flushBlock();
        repairedLines.push(line);
    }

    flushBlock();

    return repairedLines.join('\n');
};

// Helper: check whether a character index in a single line is inside a
// markdown bold/italic span (**...** or __...__). Used to avoid splitting
// valid inline markdown when applying header-heuristic fixes.
const isInsideInlineMarkdown = (line, index) => {
    const before = line.slice(0, index);
    const after = line.slice(index);

    const countDelim = (str, delim) => (str.match(new RegExp(delim, 'g')) || []).length;

    const insideDoubleStar =
        countDelim(before, '\\*\\*') % 2 === 1 && countDelim(after, '\\*\\*') >= 1;
    const insideDoubleUnderscore =
        countDelim(before, '__') % 2 === 1 && countDelim(after, '__') >= 1;

    return insideDoubleStar || insideDoubleUnderscore;
};

export const preprocessMessage = (content) => {
    if (!content) return "";

    let processed = typeof content === 'string' ? content : String(content);
    let oldProcessed;

    // 0. Normalize smart quotes/apostrophes that often appear before bullets
    // e.g. "' • item" -> "• item"
    processed = processed.replace(/^[\s]*[\u2018\u2019']\s*(?=[•·▪◦*-]\s+)/gm, '');

    // 0b. Normalize unicode bullet glyphs into markdown list items
    // This ensures ReactMarkdown renders proper <ul>/<li> lists in export.
    processed = processed.replace(/^[\s]*[•·▪◦]\s+/gm, '- ');

    // Remove orphan percentage fragments such as "- 13.6%". These are
    // duplicated CAGR values with no metric label or source context.
    processed = processed.replace(/^\s*[-*]\s+[-+]?\d+(?:\.\d+)?%\s*$/gm, '');

    // 1. Ensure newlines before headers (e.g. "text### Header" -> "text\n\n### Header")
    processed = processed.replace(/([^\n])\s*(#{1,6}\s)/g, '$1\n\n$2');

    // 1b. Split jammed headers from body text on the same line
    // e.g. "#### Market Size and Growth The global..." -> "#### Market Size and Growth\n\nThe global..."
    processed = processed.replace(
        /(#{1,6}\s.{3,60}?)(?=\s+(?:The|A|An|This|These|In|According|Another|It|There|With|For|By|From|Over|During|After|Before|At|On|Under|Some|Many|Most|All|Several|Few|About|Of|To)\s+[a-z0-9])/g,
        '$1\n\n'
    );

    // NOTE: rules 1c and 1d (merge split bold) are now applied near the end
    // of preprocessing so that later heuristic rules (e.g. inline header
    // splitting) do not permanently break bold spans.

    // 2. Ensure newlines before list items if jammed (e.g. "text- item" -> "text\n- item")
    // Only apply when the '-' is clearly a list item (space after it)
    // Be careful not to break hyphenated words
    // [^\S\n]+ = non-newline whitespace only, so already-separated bullets are never touched
    processed = processed.replace(/([^\n])([^\S\n]+)(-\s+)/g, '$1\n$3');

    // 3. Ensure newlines before table start (e.g. "text| Col |" -> "text\n\n| Col |")
    // The [^\n|] prevents matching inside existing table rows (where prev char is |)
    processed = processed.replace(/([^\n|])\s*(\n?\|[^|\n]+\|[^|\n]+\|)/g, '$1\n\n$2');

    // 4. Fix split table rows (lines starting with pipe but NOT ending with pipe)
    // iterate to handle multi-line splits
    do {
        oldProcessed = processed;
        processed = processed.replace(/^(\s*\|.*[^|\s])\s*\n\s*(\|)/gm, '$1 $2');
    } while (oldProcessed !== processed && processed.length < oldProcessed.length);

    // 5. Handle split table headers (The "Grand Fix") - still needed for valid rows split across lines
    // Match lines that look like headers/rows before a separator line
    processed = processed.replace(/(\^|\n)(\|.*)\n(\|.*)(?=\n\|[-:| ]+\|)/g, '$1$2 $3');
    processed = processed.replace(/(\^|\n)(\|.*)\n(\|.*)(?=\n\|[-:| ]+\|)/g, '$1$2 $3');

    // 6. Fix table headers/separators alignment
    processed = processed.replace(/(\|\s*)(\|[-:]+[-| :]*\|)/g, '$1\n$2');

    // 7. Fix table separators/body
    processed = processed.replace(/(\|[-:]+[-| :]*\|)\s+(\|)/g, '$1\n$2');

    // 8. Fix table body rows jammed together
    processed = processed.replace(/(\|\s+)(\|)(?![ \t]*\|)/g, '$1\n$2');

    // 9. Fix gapped table rows (double newlines between rows)
    do {
        oldProcessed = processed;
        processed = processed.replace(/(\|\s*)\n{2,}(\s*\|)/g, '$1\n$2');
    } while (oldProcessed !== processed && processed.length < oldProcessed.length);

    // NEW: AGGRESSIVE: Fix jammed rows "||" or "| |" that previous rules missed
    processed = processed.replace(/\|[ \t]*\|/g, '|\n|');

    // NEW: Ensure we don't have multiple pipes at end of line "||"
    processed = processed.replace(/\|\s*\|$/gm, '|');

    // 10. Handle the specific case where text might have a colon ending line before the table
    processed = processed.replace(/(:\\s*)\\n(\\|)/g, '$1\n\n$2');

    // 11. Fix inline "Insights" headers
    // e.g. "text Insights" -> "text\n\nInsights"
    // Skip if "Insights" is inside **...** or __...__ so valid markdown is preserved.
    processed = processed.replace(/([^\n])([^\S\n]+)(Insights)/g, (match, beforeChar, _ws, headerText, offset, string) => {
        const lineStart = string.lastIndexOf('\n', offset) + 1;
        const line = string.slice(lineStart, string.indexOf('\n', offset) === -1 ? undefined : string.indexOf('\n', offset));
        if (isInsideInlineMarkdown(line, offset - lineStart)) return match;
        if (line.trim().startsWith('|')) return match; // never split inside a table row
        return `${beforeChar}\n\n${headerText}`;
    });

    // NEW: "Insights 1. Title" -> "Insights\n\n1. Title"
    processed = processed.replace(/(Insights)\s+(\d+\.)/g, (match, headerText, numberText, offset, string) => {
        const lineStart = string.lastIndexOf('\n', offset) + 1;
        const line = string.slice(lineStart, string.indexOf('\n', offset) === -1 ? undefined : string.indexOf('\n', offset));
        if (isInsideInlineMarkdown(line, offset - lineStart)) return match;
        if (line.trim().startsWith('|')) return match; // never split inside a table row
        return `${headerText}\n\n${numberText}`;
    });

    // 12. Fix inline Report Headers (Key Segments, Conclusion, etc.)
    const headers = ["Key Segments", "Conclusion", "Market Overview", "Introduction"];
    headers.forEach(header => {
        // 1. Force newline before header, but not inside **...** or __...__
        const regex = new RegExp(`([^\\n])[^\\S\\n]+(${header})`, 'g');
        processed = processed.replace(regex, (match, beforeChar, headerText, offset, string) => {
            const lineStart = string.lastIndexOf('\n', offset) + 1;
            const line = string.slice(lineStart, string.indexOf('\n', offset) === -1 ? undefined : string.indexOf('\n', offset));
            if (isInsideInlineMarkdown(line, offset - lineStart)) return match;
        if (line.trim().startsWith('|')) return match; // never split inside a table row
            return `${beforeChar}\n\n${headerText}`;
        });

        // 2. Force newline after header if followed by a number (e.g. "Key Segments 1.")
        const splitRegex = new RegExp(`(${header})\\s+(\\d+\\.)`, 'g');
        processed = processed.replace(splitRegex, (match, headerText, numberText, offset, string) => {
            const lineStart = string.lastIndexOf('\n', offset) + 1;
            const line = string.slice(lineStart, string.indexOf('\n', offset) === -1 ? undefined : string.indexOf('\n', offset));
            if (isInsideInlineMarkdown(line, offset - lineStart)) return match;
        if (line.trim().startsWith('|')) return match; // never split inside a table row
            return `${headerText}\n\n${numberText}`;
        });
    });

    // 12b. Fix broken "Insights |" lines that split table headers/rows
    processed = processed.replace(/(\|.*)\n\s*Insights\s*\|/g, '$1 Insights |');
    processed = processed.replace(/^\s*Insights\s*\|\s*$/gm, '');

    // 12c. Split headings/separators welded onto the end of table rows
    processed = processed.replace(/(\|.*\|)\s+(#{1,6}\s+.*)/g, '$1\n\n$2');
    processed = processed.replace(/(\|.*\|)\s+(---\s+.*)/g, '$1\n\n$2');

    // 14. Robust inline numbered list fixer
    processed = fixInlineNumberedLists(processed);

    // 15. Fix specific "Sentence End + Number" patterns — skip heading lines
    processed = processed.replace(/^(?!#)(.*)(\.|:)\s+(\d+\.)/gm, (m, pre, punc, num) => `${pre}${punc}\n${num}`);

    // 15c. Fix inline Roman-numeral list items (e.g., VI. Conclusion)
    processed = processed.replace(/([^\n])\s*((?:[IVXLCDM]{2,5})\.\s+[A-Za-z])/g, '$1\n$2');

    // 15d. Split inline horizontal rules / separators
    processed = processed.replace(/([^\n])[^\S\n]*---[^\S\n]*(?=[A-Z])/g, '$1\n\n---\n\n');

    // 15e. Repair malformed conclusion/header fragments from model output
    // e.g. "Conclusion** Text..." -> "## Conclusion\n\nText..."
    processed = processed.replace(/(^|\n)\s*Conclusion\*\*\s*/gi, '$1## Conclusion\n\n');
    processed = processed.replace(/(^|\n)\s*\*\*Conclusion\*\*\s*/gi, '$1## Conclusion\n\n');

    // 15f. Repair malformed source headers after separators
    // e.g. "--- Sources and References" or "--- **Sources and References"
    processed = processed.replace(/(^|\n)\s*---\s*\*{0,2}(Sources and References)\*{0,2}\s*$/gim, '$1## $2');

    // 15g. Remove orphan separator/index artifacts such as "--- **4."
    processed = processed.replace(/^\s*-{2,}\s*\*{0,2}\d+\.?\s*\*{0,2}\s*$/gm, '');

    // 15h. Normalize accidental trailing separator tokens in sentences
    // e.g. "... support for remote education. ---" -> "... support for remote education."
    processed = processed.replace(/\s+---\s*$/gm, '');

    // 15i. Split inline bold section headers from preceding paragraph text.
    // e.g. "...in the market. **1. Market Size**" -> "...in the market.\n\n**1. Market Size**"
    processed = processed.replace(/([.!?])\s+(\*\*\d+\.\s+[^*]+\*\*)/g, '$1\n\n$2');

    // 15j. Nest flat list items under bold list-item headers.
    // A line like "- **Global AI Market Overview:**" is treated as a header;
    // subsequent "- ..." lines until the next header/blank line become indented sub-items.
    const nestListUnderBoldHeaders = (text) => {
        const lines = text.split('\n');
        const out = [];
        let i = 0;
        const headerRegex = /^\s*[-*]\s+\*\*[^*]+:\*\*\s*$/;
        const listItemRegex = /^\s*[-*]\s+/;
        while (i < lines.length) {
            const line = lines[i];
            out.push(line);
            if (headerRegex.test(line)) {
                i++;
                while (i < lines.length) {
                    const next = lines[i];
                    if (next.trim() === '') break;
                    if (headerRegex.test(next)) break;
                    if (listItemRegex.test(next)) {
                        out.push(next.replace(/^(\s*)[-*]\s+/, '$1  - '));
                    } else {
                        out.push(next);
                    }
                    i++;
                }
            } else {
                i++;
            }
        }
        return out.join('\n');
    };
    processed = nestListUnderBoldHeaders(processed);

    // 16. Fix artifacts where "-" is used as null but formatting makes it a list item "- |"
    // Remove lines that are just "- |" or "* |" or "- | - |" (garbage list lines)
    // This connects orphaned table parts.
    processed = processed.replace(/^\s*[-*]\s*\|(?:\s*\|?)*\s*$/gm, '');

    // 17. Transpose "Vertical" tables (2 columns, many rows) to Horizontal
    // Algorithm: Scan line-by-line to detect table blocks, check if 2-col, then transpose.
    const lines = processed.split('\n');
    let newLines = [];
    let currentBlock = [];

    const isTableLine = (line) => line.trim().match(/^\|.*\|$/);

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (isTableLine(line)) {
            currentBlock.push(line);
        } else {
            if (currentBlock.length > 0) {
                // End of a block -> process it
                const blockString = currentBlock.join('\n');
                const transposed = transposeTableBlock(blockString);
                newLines.push(transposed);
                currentBlock = [];
            }
            newLines.push(line);
        }
    }
    // Handle trailing block
    if (currentBlock.length > 0) {
        const blockString = currentBlock.join('\n');
        const transposed = transposeTableBlock(blockString);
        newLines.push(transposed);
    }

    processed = newLines.join('\n');

    // 18. Repair malformed tables where one logical row was split into multiple
    // pipe lines, often with stray bullet prefixes or blank lines between them.
    processed = repairBrokenTables(processed);

    // 19. Fix split bold text across lines (run late so earlier rules can't break it).
    // e.g. "**Focus\non Sustainability**" -> "**Focus on Sustainability**"
    // NOTE: exclude `|` from the matched spans so this never joins across table
    // cells/rows (e.g. "**Monday** | ... |\n| **Tuesday** ..."). Without the `|`
    // guard, the greedy `[^\n*]` would swallow whole table rows into one line.
    do {
        oldProcessed = processed;
        processed = processed.replace(/(\*\*[^\n*|]+)\n([^\n*|]+\*\*)/g, '$1 $2');
    } while (oldProcessed !== processed);

    // 20. Fix split alternate bold text across lines.
    // e.g. "__Focus\non Sustainability__" -> "__Focus on Sustainability__"
    // Same `|` guard as rule 19 so table rows are never merged.
    do {
        oldProcessed = processed;
        processed = processed.replace(/(__[^\n_|]+)\n([^\n_|]+__)/g, '$1 $2');
    } while (oldProcessed !== processed);

    return processed;
};
